import React from 'react';
import './CatecheseItemCard.css';

/**
 * CatecheseItemCard Component
 * 
 * Displays a single catechism item with all its fields
 * Shows the etappe as a badge and lists all optional fields
 * 
 * @param {Object} item - The item data to display
 * @param {string} item.etappe - The etappe/stage
 * @param {string} item.datum - The date
 * @param {string} [item.bisdom] - Diocese (optional)
 * @param {string} [item.parochie] - Parish (optional)
 * @param {string} [item.gemeenschap] - Community (optional)
 * @param {string} [item.catechistVoornaam] - Catechist first name (optional)
 * @param {string} [item.catechistAchternaam] - Catechist last name (optional)
 * @param {string} [item.catechistEmail] - Catechist email (optional)
 * @param {Function} onEdit - Callback when edit button is clicked
 * @param {boolean} canEdit - Whether the current user can edit this item
 */
const CatecheseItemCard = ({ item, onEdit, canEdit }) => {
  const catechistNaam = [item.catechistVoornaam, item.catechistAchternaam]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

  /**
   * Formats a date object or timestamp to a localized Dutch date string
   * @param {Date|Timestamp|string} date - The date to format
   * @returns {string} Formatted date string in Dutch locale
   */
  const formatDate = (date) => {
    if (!date) return '';
    // Handle Firestore Timestamp objects and regular dates
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="item-card">
      <div className="item-card-content">
        <div className="item-card-header">
          <span className="item-etappe-badge">{item.etappe}</span>
          {item.datum && (
            <span className="item-date">{formatDate(item.datum)}</span>
          )}
        </div>
        <div className="item-card-body">
          {/* Location group */}
          {(item.bisdom || item.parochie || item.gemeenschap) && (
            <div className="field-group">
              {item.bisdom && (
                <div className="item-field">
                  <span className="field-label">Bisdom:</span>
                  <strong className="field-value">{item.bisdom}</strong>
                </div>
              )}
              {item.parochie && (
                <div className="item-field">
                  <span className="field-label">Parochie:</span>
                  <strong className="field-value">{item.parochie}</strong>
                </div>
              )}
              {item.gemeenschap && (
                <div className="item-field">
                  <span className="field-label">Gemeenschap:</span>
                  <strong className="field-value">{item.gemeenschap}</strong>
                </div>
              )}
            </div>
          )}
          
          {/* People group */}
          {(catechistNaam || item.catechistEmail) && (
            <div className="field-group">
              {catechistNaam && (
                <div className="item-field">
                  <span className="field-label">Catechist:</span>
                  <strong className="field-value">{catechistNaam}</strong>
                </div>
              )}
              {item.catechistEmail && (
                <div className="item-field">
                  <span className="field-label">Catechist email:</span>
                  <strong className="field-value">{item.catechistEmail}</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {canEdit && (
        <button className="edit-button" onClick={() => onEdit(item)} title="Bewerken">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.5 1.5L14.5 4.5L5 14H2V11L11.5 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
};

export default CatecheseItemCard;
